import React, { useState } from 'react';
import {
  Button,
  DatePicker,
  Card,
  Space,
  Statistic,
  Table,
  Badge,
  Empty,
  message,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../context/AuthContext';

interface PredictionItem {
  category: string;
  predicted_amount: number;
}

interface PredictionsResponse {
  total_predicted: number;
  confidence: 'low' | 'medium' | 'high';
  by_category: PredictionItem[];
}

const Reports: React.FC = () => {
  const { token } = useAuth();
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(dayjs().subtract(3, 'months'));
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [predictions, setPredictions] = useState<PredictionsResponse | null>(null);
  const [predictionsLoading, setPredictionsLoading] = useState(false);
  const [predictionsError, setPredictionsError] = useState<string | null>(null);

  const handleExportCSV = async () => {
    if (!fromDate || !toDate) {
      message.error('Please select both dates');
      return;
    }

    try {
      const from = fromDate.format('YYYY-MM-DD');
      const to = toDate.format('YYYY-MM-DD');

      const response = await fetch(
        `/api/v1/budget/reports/export?from=${from}&to=${to}&format=csv`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export CSV');
      }

      const csvText = await response.text();
      const blob = new Blob([csvText], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget-${from}-${to}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      message.success('CSV downloaded successfully');
    } catch (error) {
      message.error('Failed to download CSV');
    }
  };

  const handleExportJSON = async () => {
    if (!fromDate || !toDate) {
      message.error('Please select both dates');
      return;
    }

    try {
      const from = fromDate.format('YYYY-MM-DD');
      const to = toDate.format('YYYY-MM-DD');

      const response = await fetch(
        `/api/v1/budget/reports/export?from=${from}&to=${to}&format=json`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to export JSON');
      }

      const jsonData = await response.json();
      const jsonText = JSON.stringify(jsonData, null, 2);
      const blob = new Blob([jsonText], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `budget-${from}-${to}.json`;
      a.click();
      URL.revokeObjectURL(url);

      message.success('JSON downloaded successfully');
    } catch (error) {
      message.error('Failed to download JSON');
    }
  };

  const loadPredictions = async () => {
    if (!token) return;

    setPredictionsLoading(true);
    setPredictionsError(null);
    try {
      const response = await fetch('/api/v1/budget/reports/predictions', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 422) {
        setPredictionsError('Need at least 3 months of historical data to generate predictions');
        setPredictions(null);
        setPredictionsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to load predictions');
      }

      const data = await response.json();
      setPredictions(data);
    } catch (error) {
      message.error('Failed to load spending predictions');
      setPredictions(null);
    } finally {
      setPredictionsLoading(false);
    }
  };

  const confidenceColor = {
    low: 'error',
    medium: 'warning',
    high: 'success',
  } as const;

  const predictionColumns: ColumnsType<PredictionItem> = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Predicted Amount',
      dataIndex: 'predicted_amount',
      key: 'predicted_amount',
      render: (amount) => `$${amount.toFixed(2)}`,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <h1>Reports & Insights</h1>

      <Card style={{ marginBottom: '24px' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <h3>Export Data</h3>
            <Space>
              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                placeholder="From Date"
              />
              <DatePicker
                value={toDate}
                onChange={setToDate}
                placeholder="To Date"
              />
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportCSV}
              >
                Download CSV
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExportJSON}
              >
                Download JSON
              </Button>
            </Space>
          </div>
        </Space>
      </Card>

      <Card
        title="Spending Prediction"
        extra={
          <Button type="primary" onClick={loadPredictions} loading={predictionsLoading}>
            Load Predictions
          </Button>
        }
      >
        {predictionsLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <Spin />
          </div>
        ) : predictionsError ? (
          <Empty description={predictionsError} />
        ) : predictions ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', gap: '32px' }}>
              <Statistic
                title="Predicted Next Month Total"
                value={predictions.total_predicted}
                prefix="$"
                precision={2}
              />
              <div>
                <h4>Confidence Level</h4>
                <Badge
                  status={confidenceColor[predictions.confidence]}
                  text={
                    predictions.confidence.charAt(0).toUpperCase() +
                    predictions.confidence.slice(1)
                  }
                />
              </div>
            </div>

            <div style={{ marginTop: '24px' }}>
              <h4>Predictions by Category</h4>
              <Table<PredictionItem>
                columns={predictionColumns}
                dataSource={predictions.by_category.map((item, idx) => ({
                  ...item,
                  key: idx,
                }))}
                pagination={false}
                bordered
              />
            </div>
          </Space>
        ) : (
          <Empty description="Click 'Load Predictions' to see spending forecast" />
        )}
      </Card>
    </div>
  );
};

export default Reports;
